import type { ComponentRef } from '@angular/core';
import {
  createComponent,
  DestroyRef,
  Directive,
  effect,
  EnvironmentInjector,
  inject,
  Injector,
  input,
  signal,
} from '@angular/core';

import { ListItemResolverDirective } from '../../default-templates/list-item/list-item-resolver.directive';
import type { Select2ListItemDirective } from '../../default-templates/list-item/list-item.directive';
import { SelectController } from '../../services/select-controller.service';
import {
  SELECT_FILTERED_ITEMS,
  SELECT_ITEMS,
  SelectItemsService,
} from '../../services/select-items.service';
import { SelectTemplatesService } from '../../services/select-templates.service';
import type { Select2Item } from '../../types';
import { GroupsListItemWrapperComponent } from './list-item-wrapper/groups-list-item-wrapper.component';
import type { GroupsFeatureDir } from './types';
import { GROUPS_FEATURE } from './types';

export interface Select2GroupWithItems<T extends Select2Item = Select2Item> {
  name: string;
  items: T[];
}

@Directive({
  standalone: true,
  selector: 'rt-select2[withGroups]',
  providers: [
    { provide: SELECT_FILTERED_ITEMS, useFactory: () => signal<Select2Item[]>([]) },
    { provide: SELECT_ITEMS, useFactory: () => signal<Select2Item[]>([]) },
    { provide: GROUPS_FEATURE, useExisting: GroupsFeatureDirective },
  ],
})
export class GroupsFeatureDirective implements GroupsFeatureDir {
  public groupedItems = input<Select2GroupWithItems[]>([]);
  public itemsCheck = input(<Select2Item[]>[], {
    alias: 'items',
    transform: () => {
      throw new Error('Use groupedItems input');
    },
  });

  public headers: Record<number, string> = {};
  public listItem: Select2ListItemDirective<any>;

  private wrapperRef?: ComponentRef<GroupsListItemWrapperComponent>;
  private filteredItems = inject(SELECT_FILTERED_ITEMS);
  private items = inject(SELECT_ITEMS);

  constructor(
    public selectItemsService: SelectItemsService,
    public selectTemplatesService: SelectTemplatesService,
    public selectController: SelectController,
    private listItemResolver: ListItemResolverDirective,
    private injector: Injector,
    private envInjector: EnvironmentInjector,
  ) {
    effect(
      () => {
        const flatItems = this.groupedItems().flatMap(group => group.items);
        this.items.set(flatItems);
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        this.headers = {};
        let nextGroupIndex = 0;
        const items = this.groupedItems().flatMap(group => {
          const filteredGroup = this.selectItemsService.filterItems(group.items);
          const groupSize = filteredGroup.length;
          if (groupSize) {
            this.headers = {
              ...this.headers,
              [nextGroupIndex]: group.name,
            };
            nextGroupIndex += groupSize;
          }
          return filteredGroup;
        });
        this.filteredItems.set(items);
      },
      { allowSignalWrites: true },
    );

    inject(DestroyRef).onDestroy(() => {
      this.wrapperRef?.destroy();
    });
  }

  public ngAfterContentInit() {
    this.wrapperRef = createComponent(GroupsListItemWrapperComponent, {
      environmentInjector: this.envInjector,
      elementInjector: this.injector,
    });
    this.wrapperRef.changeDetectorRef.detectChanges();
    this.selectTemplatesService.listItemDirective = this.wrapperRef.instance.directive();
    this.listItem = this.listItemResolver.resolve();
    this.selectController.detectChanges$.next();
  }
}
